//    "verification_code": "eyJ1aWQiOiIzMTk0MSIsImV4cCI6MTc2NDI1MDcyNSwibm9uY2UiOiIzNjMwNTI3ODUwNTI1ODZjNGUzNjQyNzU2NjYyNjk1OSJ9LgVQGnLWc_HxVMv3kgQqqmhjmElkmExR66maEAbyJZbH"
import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { ROUTES } from "@/core/constants/app";
import { imageList } from "@/core/utils/imageList";
import Loader from "@/core/components/loader/Loader";
import "./Activation.scss";
import { SEO } from "@/core/components/SEO/SEO";

type ActivationState = "loading" | "success" | "error" | "invalid";

export const Activation = () => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ActivationState>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState<string>("");

  const verificationCode = searchParams.get("token");

  useEffect(() => {
    if (!verificationCode) {
      setState("invalid");
      return;
    }

    verifyEmail();
  }, [verificationCode]);

  const verifyEmail = async () => {
    try {
      setState("loading");

      const response = await apiService.post(
        API_ENDPOINTS.SIGNUP.EMAIL_VERIFICATION,
        {
          token: verificationCode,
        },
      );

      //console.log("Email verification successful:", response);
      setState("success");
    } catch (error: any) {
      console.error("Email verification failed:", error);

      let message = "Verification failed. Please try again.";

      if (error?.response?.status === 400) {
        message = "Invalid or expired verification link.";
      } else if (error?.response?.status === 404) {
        message = "Verification link not found.";
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      }

      setErrorMessage(message);
      setState("error");
    }
  };

  const handleResendVerification = async () => {
    if (!email.trim()) {
      alert("Please enter your email address");
      return;
    }

    try {
      setIsResending(true);

      // You might need to implement a resend endpoint
      // For now, this is a placeholder
      await apiService.post(API_ENDPOINTS.SIGNUP.REQUEST_PASSWORD_RESET, {
        email: email,
      });

      alert("Verification email sent! Please check your inbox.");
    } catch (error: any) {
      console.error("Resend verification failed:", error);
      alert("Failed to resend verification email. Please try again later.");
    } finally {
      setIsResending(false);
    }
  };

  const renderContent = () => {
    switch (state) {
      case "loading":
        return <Loader message="Verifying your email..." />;

      case "success":
        return (
          <div className="activation-content success">
            <div className="activation-icon success">
              <i className="pi pi-check-circle"></i>
            </div>
            <h1 className="activation-title">Email Verified!</h1>
            <p className="activation-message">
              Your email address has been successfully verified. You can now log
              in to your account.
            </p>
            <div className="activation-actions">
              <Link to={ROUTES.LOGIN} className="btn-primary">
                Go to Login
              </Link>
            </div>
          </div>
        );

      case "error":
        return (
          <div className="activation-content error">
            <div className="activation-icon error">
              <i className="pi pi-times-circle"></i>
            </div>
            <h1 className="activation-title">Verification Failed</h1>
            <p className="activation-message">{errorMessage}</p>
            <div className="activation-actions">
              <button
                className="btn-secondary"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
              <Link to={ROUTES.LOGIN} className="btn-primary">
                Go to Login
              </Link>
            </div>

            <div className="resend-section">
              <p className="resend-text">Need a new verification email?</p>
              <div className="resend-form">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="email-input"
                />
                <button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="btn-outline"
                >
                  {isResending ? "Sending..." : "Resend Email"}
                </button>
              </div>
            </div>
          </div>
        );

      case "invalid":
        return (
          <div className="activation-content invalid">
            <div className="activation-icon warning">
              <i className="pi pi-exclamation-triangle"></i>
            </div>
            <h1 className="activation-title">Invalid Link</h1>
            <p className="activation-message">
              The verification link is invalid or missing. Please check the link
              in your email or contact support.
            </p>
            <div className="activation-actions">
              <Link to={ROUTES.SIGNUP} className="btn-secondary">
                Sign Up Again
              </Link>
              <Link to={ROUTES.LOGIN} className="btn-primary">
                Go to Login
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="activation-page">
      <SEO
        title={`Activate account | EUcalls`}
        description={`Activate account`}
      />

      <div className="activation-container">
        <div className="activation-header">
          <img src={imageList.logo} alt="EuCalls" className="activation-logo" />
        </div>

        {renderContent()}

        <div className="activation-footer">
          <p>
            &copy;{new Date().getFullYear()} EUcalls. All rights reserved.
            Powered by{" "}
            <a href="https://joistpark.eu/" target="_blank">
              JOIST
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
